import { IFilterOptionDef } from 'ag-grid-community';
import moment from 'moment-timezone';

export class CommaSeparatedNumberFilterValue {
    constructor(public readonly raw: string) {}

    valueOf(): number {
        return 0;
    }

    toString(): string {
        return this.raw;
    }
}

export function extractCommaSeparatedRawValue(value: any): any {
    if (value && typeof value === 'object' && 'raw' in value) {
        return (value as { raw: string }).raw;
    }
    return value;
}

export class AgGridFilterParams {
    static blankFilterOptionDef: IFilterOptionDef = {
        displayKey: 'blank',
        displayName: 'Blank',
        numberOfInputs: 0,
        predicate: (filterValue: any, cellValue: any) => {
            return !cellValue;
        },
    };

    static notBlankFilterOptionDef: IFilterOptionDef = {
        displayKey: 'notBlank',
        displayName: 'Not Blank',
        numberOfInputs: 0,
        predicate: (filterValue: any, cellValue: any) => {
            return cellValue;
        },
    };

    static commaSeparatedFilterOptionDef: IFilterOptionDef = {
        displayKey: 'Comma Separated',
        displayName: 'Comma Separated',
        numberOfInputs: 1,
        predicate: (filterValues: any[], cellValue: any) => {
            const filterString = filterValues[0];
            if (!filterString) {
                return false;
            }
            const cellValues = cellValue
                .trim()
                .split(',')
                .filter((v: string) => v.trim() !== '');
            return filterString
                .split(',')
                .filter((x) => x.trim() !== '')
                .some((x) => cellValues.includes(x.trim()));
        },
    };

    static commaSeparatedNumberFilterOptionDef: IFilterOptionDef = {
        displayKey: 'Comma Separated',
        displayName: 'Comma Separated',
        numberOfInputs: 1,
        predicate: (filterValues: any[], cellValue: any) => {
            const filterString = extractCommaSeparatedRawValue(filterValues[0]);
            if (filterString === null || filterString === undefined || filterString === '') {
                return false;
            }
            if (cellValue === null || cellValue === undefined || cellValue === '') {
                return false;
            }
            return String(filterString)
                .split(',')
                .filter((v) => v.trim() !== '')
                .some((v) => Number(v.trim()) === Number(cellValue));
        },
    };

    static yesOptionDef: IFilterOptionDef = {
        displayKey: 'yes',
        displayName: 'Yes',
        numberOfInputs: 0,
        predicate: (filterValue: any, cellValue: any) => {
            return cellValue && (cellValue.toLowerCase() === 'yes' || cellValue.toLowerCase() === 'true');
        },
    };

    static noOptionDef: IFilterOptionDef = {
        displayKey: 'no',
        displayName: 'No',
        numberOfInputs: 0,
        predicate: (filterValue: any, cellValue: any) => {
            return cellValue && (cellValue.toLowerCase() === 'no' || cellValue.toLowerCase() === 'false');
        },
    };

    static noFilterOptionDef: IFilterOptionDef = {
        displayKey: ' ',
        displayName: ' ',
        numberOfInputs: 0,
        predicate: (filterValue: any, cellValue: any) => {
            return true;
        },
    };

    static textFilterParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: ['startsWith', 'equals', 'notEqual', 'contains', 'notContains', 'endsWith'],
        defaultOption: 'contains',
        newRowsAction: 'keep',
    };

    static get textFilterParamsSmartSearch(): any {
        const params = { ...AgGridFilterParams.textFilterParams };
        params.filterOptions = [...params.filterOptions, 'smartSearch'];
        params.defaultOption = 'smartSearch';
        return params;
    }

    static textFilterParamsStartsWith: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: ['startsWith', 'equals', 'notEqual', 'contains', 'notContains', 'endsWith'],
        defaultOption: 'startsWith',
        newRowsAction: 'keep',
    };

    static textFilterWithCommaParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'startsWith',
            'equals',
            'notEqual',
            'contains',
            'notContains',
            'endsWith',
            AgGridFilterParams.commaSeparatedFilterOptionDef,
        ],
        defaultOption: 'contains',
        newRowsAction: 'keep',
    };

    static numberWithRangeAndEqualToAndBlankAndCommaFilterParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThan',
            'greaterThanOrEqual',
            'lessThan',
            'lessThanOrEqual',
            'inRange',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
            AgGridFilterParams.commaSeparatedNumberFilterOptionDef,
        ],
        newRowsAction: 'keep',
    };

    static textFilterWithBlankOptionsParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'startsWith',
            'equals',
            'notEqual',
            'contains',
            'notContains',
            'endsWith',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        defaultOption: 'contains',
        newRowsAction: 'keep',
    };

    static textFilterWithBlankOptionsAndCommaParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'startsWith',
            'equals',
            'notEqual',
            'contains',
            'notContains',
            'endsWith',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
            AgGridFilterParams.commaSeparatedFilterOptionDef,
        ],
        defaultOption: 'contains',
        newRowsAction: 'keep',
    };

    static textFloatingFilterParams: any = {};

    // Parses a cell value into a local midnight Date for comparison against ag-Grid's
    // date filter. Supports the legacy 'M/D/YYYY' string format (used when a column's
    // cellValue is pre-formatted) as well as raw Date objects / ISO date strings coming
    // directly from API row data (see #585056 — some columns filter on the raw field
    // value, not a formatted string, so a plain '/'.split() silently produced an Invalid
    // Date and every row failed the filter).
    //
    // `displayTimeZone` must match the time zone the column's valueFormatter uses to
    // display the date (e.g. 'America/Chicago' for fields shown in Central Time, or
    // 'UTC' for fields displayed via the 'stored'/'utc' WeissmanDateFormat option).
    // Extracting the calendar day in the wrong time zone can shift the day by one,
    // making the calendar day used for filtering not match what the user sees on screen.
    private static parseFilterCellDate(cellValue: any, displayTimeZone: string = 'UTC'): Date {
        if (cellValue === null || cellValue === undefined || cellValue === '') {
            return null;
        }

        if (typeof cellValue === 'string' && cellValue.indexOf('/') !== -1) {
            // Formatted strings may include a time portion (e.g. '7/28/2026 7:33 AM' for
            // DateTime/UTCDateTime columns) - only the date portion (before any space) is
            // relevant for the calendar-day comparison below. Splitting the full string on
            // '/' without stripping the time first corrupts the year segment (e.g. parsing
            // '2026 7:33 AM' as a number), producing an Invalid Date that always fails the
            // filter comparison.
            const datePortion = cellValue.split(' ')[0];
            const dateParts = datePortion.split('/');
            return new Date(Number(dateParts[2]), Number(dateParts[0]) - 1, Number(dateParts[1]));
        }

        const parsedMoment = moment.tz(cellValue, displayTimeZone);
        if (!parsedMoment.isValid()) {
            return null;
        }

        return new Date(parsedMoment.year(), parsedMoment.month(), parsedMoment.date());
    }

    private static buildDateComparator(displayTimeZone: string = 'UTC') {
        return (filterLocalDateAtMidnight: Date, cellValue: any) => {
            const cellDate = AgGridFilterParams.parseFilterCellDate(cellValue, displayTimeZone);
            if (!cellDate) {
                return -1;
            }
            if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
                return 0;
            }
            if (cellDate < filterLocalDateAtMidnight) {
                return -1;
            }
            if (cellDate > filterLocalDateAtMidnight) {
                return 1;
            }
            return -1;
        };
    }

    static dateFilterParams: any = {
        clearButton: true,
        applyButton: true,
        suppressAndOrCondition: true,
        filterOptions: ['equals', 'notEqual', 'greaterThan', 'lessThan'],
        browserDatePicker: false,
        comparator: AgGridFilterParams.buildDateComparator('UTC'),
        newRowsAction: 'keep',
    };

    static dateFilterParamsWithBlankOptionsParams: any = {
        clearButton: true,
        applyButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThan',
            'lessThan',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        browserDatePicker: false,
        comparator: AgGridFilterParams.buildDateComparator('UTC'),
        newRowsAction: 'keep',
    };

    // Use for date columns whose valueFormatter displays the date in Central Time
    // (e.g. WeissmanDateFormat(..., 'central')), so the filtered calendar day matches
    // what the user sees on screen (see #585056).
    static dateFilterParamsWithBlankOptionsCentralTimeParams: any = {
        clearButton: true,
        applyButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThan',
            'lessThan',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        browserDatePicker: false,
        comparator: AgGridFilterParams.buildDateComparator('America/Chicago'),
        newRowsAction: 'keep',
    };

    static dateFloatingFilterParams: any = {};

    static numberFilterParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: ['equals', 'notEqual', 'greaterThan', 'lessThan'],
        newRowsAction: 'keep',
    };

    static numberFilterParamsWithEqualTo: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: ['equals', 'notEqual', 'greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual'],
        newRowsAction: 'keep',
    };

    static numberFilterWithBlankOptionsParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThan',
            'lessThan',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        newRowsAction: 'keep',
    };

    static numberFilterWithEqualToAndBlankOptionsParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThan',
            'greaterThanOrEqual',
            'lessThan',
            'lessThanOrEqual',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        newRowsAction: 'keep',
    };

    static numberWithRangeFilterParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: ['equals', 'notEqual', 'greaterThan', 'lessThan', 'inRange'],
        newRowsAction: 'keep',
    };

    static numberWithRangeFilterAndEqualToParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThan',
            'greaterThanOrEqual',
            'lessThan',
            'lessThanOrEqual',
            'inRange',
        ],
        newRowsAction: 'keep',
    };

    static numberWithRangeAndBlankOptionsFilterParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThan',
            'lessThan',
            'inRange',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        newRowsAction: 'keep',
    };

    static numberWithRangeAndEqualToAndBlankFilterParams: any = {
        clearButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            'greaterThan',
            'greaterThanOrEqual',
            'lessThan',
            'lessThanOrEqual',
            'inRange',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        newRowsAction: 'keep',
    };

    static numberFloatingFilterParams: any = {};

    static booleanFilterParams: any = {
        clearButton: true,
        applyButton: true,
        suppressAndOrCondition: true,
        filterOptions: ['equals', 'notEqual'],
        defaultOption: 'equals',
        newRowsAction: 'keep',
    };

    static booleanFilterWithBlankOptionsParams: any = {
        clearButton: true,
        applyButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            'equals',
            'notEqual',
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        defaultOption: 'equals',
        newRowsAction: 'keep',
    };

    static yesNoFilterWithBlankOptionsParams: any = {
        clearButton: true,
        applyButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            AgGridFilterParams.noFilterOptionDef,
            AgGridFilterParams.yesOptionDef,
            AgGridFilterParams.noOptionDef,
            AgGridFilterParams.blankFilterOptionDef,
            AgGridFilterParams.notBlankFilterOptionDef,
        ],
        defaultOption: AgGridFilterParams.noFilterOptionDef.displayKey,
        newRowsAction: 'keep',
    };

    static yesNoFilterParams: any = {
        clearButton: true,
        applyButton: true,
        suppressAndOrCondition: true,
        filterOptions: [
            AgGridFilterParams.noFilterOptionDef,
            AgGridFilterParams.yesOptionDef,
            AgGridFilterParams.noOptionDef,
        ],
        defaultOption: AgGridFilterParams.noFilterOptionDef.displayKey,
        newRowsAction: 'keep',
    };

    static booleanFloatingFilterParams: any = {};
}
